import React, { useRef, useCallback } from 'react'
import ssapi from 'lib/api/secondstepApi'
import Select from 'react-select/lib/Async'
import { OptionTypeBase, ValueType } from 'react-select'

// TypeScript interfaces
interface SelectLookupProps {
  apiVersion?: number
  criteria?: Record<string, any>
  formatSuggestion: (item: any) => OptionTypeBase
  isMulti?: boolean
  onChange: (value: ValueType<OptionTypeBase>) => void
  placeholder: string
  searchEndpoint: string
  searchField: string
  value?: ValueType<OptionTypeBase>
}

interface User {
  email: string
  firstName: string
  lastName: string
}

const SelectLookup: React.FC<SelectLookupProps> = ({
  criteria = {},
  formatSuggestion,
  isMulti = false,
  onChange,
  placeholder,
  searchEndpoint,
  searchField,
  value,
}) => {
  const lastRequestIdRef = useRef<NodeJS.Timeout | null>(null)

  const loadSuggestions = useCallback(
    (inputValue: string, callback: (options: OptionTypeBase[]) => void) => {
      const trimmedValue = inputValue.trim().toLowerCase()

      if (trimmedValue.length < 4) {
        callback([])
        return
      }

      // Cancel the previous request
      if (lastRequestIdRef.current) {
        clearTimeout(lastRequestIdRef.current)
      }

      const searchCriteria = { ...criteria, [searchField]: trimmedValue }

      lastRequestIdRef.current = setTimeout(() => {
        // Use secondstepApi with proper authentication
        ssapi
          .searchProfiles(searchCriteria)
          .then(({ data }) => {
            const suggestions = data.map(formatSuggestion)
            callback(suggestions)
          })
          .catch(() => {
            callback([])
          })
      }, 1000)
    },
    [criteria, searchField, searchEndpoint, formatSuggestion],
  )

  return (
    <Select
      cacheOptions
      isClearable
      isMulti={isMulti}
      loadOptions={loadSuggestions}
      onChange={onChange}
      openMenuOnClick={false}
      placeholder={placeholder}
      value={value}
    />
  )
}

// Export helper functions with proper typing
export const formatUserSuggestion = (user: User): OptionTypeBase => ({
  value: user.email,
  label: `${user.email} - ${user.firstName} ${user.lastName}`,
  ...user,
})

export default SelectLookup
